import { withApollo, WithApolloClient } from "@apollo/client/react/hoc";
import { RouteComponentProps, withRouter } from "react-router-dom";
import {
  useDeletePlaylist,
  UseDeletePlaylist,
} from "src/hooks/useDeletePlaylist";
import {
  DuplicatePlaylistMutationFn,
  PlaylistByIdForListCachingQuery,
  PlaylistByIdQueryVariables,
  PublishDraftPlaylistMutationFn,
  RevertDraftPlaylistMutationFn,
  SetScreenContentByPlaylistIdMutationFn,
  UpdatePlaylistColorMutationFn,
  UpdatePlaylistEmbedMutationFn,
  UpdatePlaylistMutationFn,
  UpdatePlaylistTagsMutationFn,
  UpdateSharePlaylistToAllSpacesMutationFn,
  UpdateSharePlaylistToSpacesMutationFn,
  useDuplicatePlaylistMutation,
  usePlaylistByIdForListCachingQuery,
  usePlaylistByIdQuery,
  usePublishDraftPlaylistMutation,
  useRevertDraftPlaylistMutation,
  useSetScreenContentByPlaylistIdMutation,
  useTagsByPlaylistsQuery,
  useUpdatePlaylistColorMutation,
  useUpdatePlaylistEmbedMutation,
  useUpdatePlaylistMutation,
  useUpdatePlaylistTagsMutation,
  useUpdateSharePlaylistToAllSpacesMutation,
  useUpdateSharePlaylistToSpacesMutation,
} from "../../../types.g";
import { compose } from "../../../utils/compose";
import { useAppContext } from "src/hooks/useAppContext";

export interface PlaylistQueryInputProps {}

export interface ApolloProps
  extends RouteComponentProps<any>,
    WithApolloClient<{}>,
    UseDeletePlaylist {
  allTags: string[];
  isAvailableTagsLoading: boolean;
  playlistByIdForListCaching: PlaylistByIdForListCachingQuery["playlistById"];
  duplicatePlaylist: DuplicatePlaylistMutationFn;
  updatePlaylist: UpdatePlaylistMutationFn;
  updatePlaylistTags: UpdatePlaylistTagsMutationFn;
  updatePlaylistColor: UpdatePlaylistColorMutationFn;
  updateSharePlaylistToAllSpaces: UpdateSharePlaylistToAllSpacesMutationFn;
  updateSharePlaylistToSpaces: UpdateSharePlaylistToSpacesMutationFn;
  updatePlaylistEmbed: UpdatePlaylistEmbedMutationFn;
  publishDraftPlaylist: PublishDraftPlaylistMutationFn;
  revertDraftPlaylist: RevertDraftPlaylistMutationFn;
  setContentByPlaylistId: SetScreenContentByPlaylistIdMutationFn;
}

export const withData = compose(
  withRouter,
  withApollo,
  (Component) => (props: ApolloProps) => {
    const queryVar: PlaylistByIdQueryVariables = {
      id: props.match.params.playlistId,
    };
    const { data } = usePlaylistByIdQuery({
      variables: queryVar,
      fetchPolicy: "cache-and-network",
      skip: !props?.match?.params?.playlistId,
    });

    const result = usePlaylistByIdForListCachingQuery({
      variables: queryVar,
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-only",
      skip: !props?.match?.params?.playlistId,
    });

    return (
      <Component
        {...props}
        playlistById={data?.playlistById}
        playlistByIdForListCaching={result.data?.playlistById}
      />
    );
  },
  (Component) => (props: ApolloProps) => {
    const context = useAppContext();
    const { data, loading } = useTagsByPlaylistsQuery({
      variables: {
        spaceId: context.currentSpace?.id,
        isShared: false,
      },
    });
    return (
      <Component
        {...props}
        allTags={data?.tagsByPlaylists}
        isAvailableTagsLoading={loading}
      />
    );
  },
  (Component) => (props: ApolloProps) => {
    const { deletePlaylist } = useDeletePlaylist();
    const [duplicatePlaylist] = useDuplicatePlaylistMutation();
    const [publishDraftPlaylist] = usePublishDraftPlaylistMutation();
    const [revertDraftPlaylist] = useRevertDraftPlaylistMutation();
    const [updatePlaylist] = useUpdatePlaylistMutation();
    const [updatePlaylistColor] = useUpdatePlaylistColorMutation();
    const [updatePlaylistEmbed] = useUpdatePlaylistEmbedMutation();
    const [updatePlaylistTags] = useUpdatePlaylistTagsMutation();
    const [updateSharePlaylistToAllSpaces] =
      useUpdateSharePlaylistToAllSpacesMutation();
    const [updateSharePlaylistToSpaces] =
      useUpdateSharePlaylistToSpacesMutation();
    const [setContentByPlaylistId] = useSetScreenContentByPlaylistIdMutation();
    return (
      <Component
        {...props}
        deletePlaylist={deletePlaylist}
        duplicatePlaylist={duplicatePlaylist}
        publishDraftPlaylist={publishDraftPlaylist}
        revertDraftPlaylist={revertDraftPlaylist}
        updatePlaylist={updatePlaylist}
        updatePlaylistColor={updatePlaylistColor}
        updatePlaylistEmbed={updatePlaylistEmbed}
        updatePlaylistTags={updatePlaylistTags}
        updateSharePlaylistToAllSpaces={updateSharePlaylistToAllSpaces}
        updateSharePlaylistToSpaces={updateSharePlaylistToSpaces}
        setContentByPlaylistId={setContentByPlaylistId}
      />
    );
  },
);
